// pages/Roles/AddRole.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";

export default function AddRole({ onRefresh }) {
  const theme = useTheme();
  const [modules, setModules] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingModules, setLoadingModules] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoadingModules(true);
      const response = await axiosInstance.get('/api/modules');
      
      if (response.data.success) {
        const responseData = typeof response.data.data === 'string' 
          ? JSON.parse(response.data.data) 
          : response.data.data;
        
        setModules(responseData.modules || []);
        
        const initialPermissions = [];
        const flattenSubmodules = (mods) => {
          mods.forEach(mod => {
            if (mod.submodules && mod.submodules.length > 0) {
              mod.submodules.forEach(submod => {
                initialPermissions.push({
                  module_id: submod.id,
                  module_name: submod.name,
                  can_view: true,
                  can_add: false,
                  can_edit: false,
                  can_delete: false
                });
              });
            }
          });
        };
        flattenSubmodules(responseData.modules || []);
        
        setFormData(prev => ({
          ...prev,
          permissions: initialPermissions
        }));
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      setError("Failed to load modules");
    } finally {
      setLoadingModules(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (moduleId, permissionType, value) => {
    if (permissionType === 'can_view') return;
    
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm => 
        perm.module_id === moduleId 
          ? { ...perm, [permissionType]: value }
          : perm
      )
    }));
  };

  const handleSelectAll = (permissionType, value) => {
    if (permissionType === 'can_view') return;
    
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm => ({
        ...perm,
        [permissionType]: value
      }))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    if (!formData.name) {
      setError("Role name is required");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/api/roles', {
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions
      });
      
      if (response.data.success) {
        setSuccess("Role created successfully!");
        
        setFormData({
          name: '',
          description: '',
          permissions: formData.permissions.map(p => ({
            ...p,
            can_view: true,
            can_add: false,
            can_edit: false,
            can_delete: false
          }))
        });
        
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
            setSuccess(null);
          }, 2000);
        }
      } else {
        setError(response.data.message || "Failed to create role");
      }
    } catch (error) {
      console.error("Error creating role:", error);
      setError(error.response?.data?.message || "Error creating role. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderModulePermissions = (module, level = 0) => {
    if (level === 0) {
      return (
        <React.Fragment key={module.id}>
          <Box
            component="tr"
            sx={{
              backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#e9ecef",
            }}
          >
            <td colSpan="5" style={{ paddingLeft: '10px', fontWeight: 'bold' }}>
              <i className={`fa ${module.icon} me-2`}></i>
              {module.name}
              <Chip label="Parent Module" size="small" sx={{ ml: 1 }} color="default" />
            </td>
          </Box>
          {module.submodules && module.submodules.map(submodule => 
            renderModulePermissions(submodule, level + 1)
          )}
        </React.Fragment>
      );
    }
    
    const permission = formData.permissions.find(p => p.module_id === module.id);
    if (!permission) return null;

    return (
      <tr key={module.id}>
        <td style={{ paddingLeft: `${level * 20 + 10}px` }}>
          {module.name}
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={true}
            disabled={true}
            className="form-check-input"
            title="View permission is enabled by default for all roles"
          />
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={permission.can_add}
            onChange={(e) => handlePermissionChange(module.id, 'can_add', e.target.checked)}
            className="form-check-input"
          />
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={permission.can_edit}
            onChange={(e) => handlePermissionChange(module.id, 'can_edit', e.target.checked)}
            className="form-check-input"
          />
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={permission.can_delete}
            onChange={(e) => handlePermissionChange(module.id, 'can_delete', e.target.checked)}
            className="form-check-input"
          />
        </td>
      </tr>
    );
  };

  const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

  return (
    <Box
      sx={{
        width: '100%',
        p: 2,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.mode === 'dark' ? '#475569' : '#cbd5e1',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? '#64748b' : '#94a3b8',
          },
        },
      }}
    >
      <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
          Create New Role
        </Typography>
      </Box>
      <Box>
        
        <form onSubmit={handleSubmit}>
          <Box display="flex" gap={2} mb={3}>
            <TextField
              fullWidth
              label="Role Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              placeholder="e.g., Content Manager"
              size="small"
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={isLoading}
              placeholder="Brief description of this role"
              size="small"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Alert severity="info" sx={{ mb: 3 }}>
            <i className="fa fa-info-circle me-2"></i>
            <strong>Note:</strong> View permission is enabled by default for all submodules. 
            You can configure Add, Edit, and Delete permissions for each submodule.
          </Alert>

          <Typography variant="h6" mb={2} color="text.primary">
            Module Permissions
          </Typography>

          {loadingModules ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress size={30} />
              <Typography ml={2}>Loading modules...</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
                mb: 3,
                "& table": {
                  width: "100%",
                  borderCollapse: "collapse",
                  border: `1px solid ${theme.palette.divider}`,
                },
                "& th, & td": {
                  padding: "12px",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary,
                },
                "& th": {
                  backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                },
                "& tbody tr:hover": {
                  backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc",
                },
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Module / Submodule</th>
                    <th className="text-center" style={{ width: '120px' }}>
                      View
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        (Always On)
                      </Typography>
                    </th>
                    <th className="text-center" style={{ width: '120px' }}>
                      Add
                      <br />
                      <Button
                        size="small"
                        onClick={() => handleSelectAll('can_add', true)}
                        sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                      >
                        All
                      </Button>
                      {' | '}
                      <Button
                        size="small"
                        onClick={() => handleSelectAll('can_add', false)}
                        sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                      >
                        None
                      </Button>
                    </th>
                    <th className="text-center" style={{ width: '120px' }}>
                      Edit
                      <br />
                      <Button
                        size="small"
                        onClick={() => handleSelectAll('can_edit', true)}
                        sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                      >
                        All
                      </Button>
                      {' | '}
                      <Button
                        size="small"
                        onClick={() => handleSelectAll('can_edit', false)}
                        sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                      >
                        None
                      </Button>
                    </th>
                    <th className="text-center" style={{ width: '120px' }}>
                      Delete
                      <br />
                      <Button
                        size="small"
                        onClick={() => handleSelectAll('can_delete', true)}
                        sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                      >
                        All
                      </Button>
                      {' | '}
                      <Button
                        size="small"
                        onClick={() => handleSelectAll('can_delete', false)}
                        sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                      >
                        None
                      </Button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map(module => renderModulePermissions(module))}
                </tbody>
              </table>
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || loadingModules}
            size="large"
          >
            {isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creating Role...
              </>
            ) : (
              <>
                <i className="fa fa-plus me-2"></i>
                Create Role
              </>
            )}
          </Button>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            <i className="fa fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 3 }} onClose={() => setSuccess(null)}>
            <i className="fa fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}
      </Box>
    </Box>
  );
}